/**
 * Custom RJSF widget registry — Tailwind-styled widgets matching the Flowize design system.
 * Widgets are passed as the `widgets` prop to the RJSF Form component.
 */

import React, { useRef, useState } from 'react';
import { Upload, FolderOpen, FileText, X, Loader } from 'lucide-react';
import { smartUpload } from '../api/filesApi';
import FilePicker from '../components/FilePicker';

// Shared input class factory
const inputClass = (hasError) =>
  `w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
    hasError ? 'border-red-300' : 'border-gray-300'
  }`;

export function TextWidget({ id, value, required, disabled, readonly, onChange, onBlur, onFocus, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  return (
    <input
      id={id}
      type="text"
      value={value || ''}
      required={required}
      disabled={disabled || readonly}
      className={inputClass(hasError)}
      onChange={(e) => onChange(e.target.value || undefined)}
      onBlur={() => onBlur && onBlur(id, value)}
      onFocus={() => onFocus && onFocus(id, value)}
    />
  );
}

export function TextareaWidget({ id, value, required, disabled, readonly, onChange, onBlur, onFocus, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  return (
    <textarea
      id={id}
      value={value || ''}
      required={required}
      disabled={disabled || readonly}
      rows={3}
      className={`${inputClass(hasError)} resize-none`}
      onChange={(e) => onChange(e.target.value || undefined)}
      onBlur={() => onBlur && onBlur(id, value)}
      onFocus={() => onFocus && onFocus(id, value)}
    />
  );
}

export function SelectWidget({ id, value, required, disabled, readonly, onChange, options, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  const { enumOptions = [] } = options;
  return (
    <select
      id={id}
      value={value || ''}
      required={required}
      disabled={disabled || readonly}
      className={inputClass(hasError)}
      onChange={(e) => onChange(e.target.value || undefined)}
    >
      {!required && <option value="">Select…</option>}
      {enumOptions.map(({ value: v, label }) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );
}

export function DateWidget({ id, value, required, disabled, readonly, onChange, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  return (
    <input
      id={id}
      type="date"
      value={value || ''}
      required={required}
      disabled={disabled || readonly}
      className={inputClass(hasError)}
      onChange={(e) => onChange(e.target.value || undefined)}
    />
  );
}

export function UpDownWidget({ id, value, required, disabled, readonly, onChange, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  return (
    <input
      id={id}
      type="number"
      value={value ?? ''}
      required={required}
      disabled={disabled || readonly}
      className={inputClass(hasError)}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? undefined : Number(v));
      }}
    />
  );
}

/**
 * FileUploadWidget — uploads immediately to fileserver on file select.
 * Stores { file_id, filename } as the field value (not a data-url).
 * RJSF treats this as a string field with format: "data-url".
 */
export function FileUploadWidget({ id, value, required, disabled, readonly, onChange, rawErrors }) {
  const hasError = rawErrors && rawErrors.length > 0;
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // value can be: null, a stringified JSON { file_id, filename }, or legacy string
  let parsed = null;
  if (value) {
    try {
      parsed = typeof value === 'string' ? JSON.parse(value) : value;
    } catch {
      parsed = { filename: value };
    }
  }
  const displayName = parsed?.filename || parsed?.file_id || null;

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const result = await smartUpload(file, null, 100 * 1024 * 1024);
      const stored = JSON.stringify({ file_id: result.file_id, filename: file.name });
      onChange(stored);
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePickerSelect = (file) => {
    const stored = JSON.stringify({ file_id: file.file_id || file.id, filename: file.filename || file.name });
    onChange(stored);
    setShowPicker(false);
  };

  const handleClear = () => onChange(undefined);

  return (
    <div className={`space-y-2 ${hasError ? 'border border-red-300 rounded-lg p-2' : ''}`}>
      {displayName && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <FileText size={14} className="text-blue-600 shrink-0" />
          <span className="text-blue-800 truncate flex-1">{displayName}</span>
          <button
            type="button"
            onClick={handleClear}
            className="text-blue-400 hover:text-blue-700 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled || readonly}
          onClick={() => setShowPicker(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
        >
          <FolderOpen size={14} />
          Choose from library
        </button>
        <button
          type="button"
          disabled={disabled || readonly || uploading}
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
        >
          {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading…' : 'Upload new'}
        </button>
      </div>
      <input
        type="file"
        className="hidden"
        ref={fileInputRef}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUpload(file);
          e.target.value = '';
        }}
      />
      <FilePicker
        show={showPicker}
        onSelect={handlePickerSelect}
        onClose={() => setShowPicker(false)}
      />
    </div>
  );
}

/**
 * Map of RJSF widget names to custom components.
 * Pass this as the `widgets` prop to the RJSF Form.
 */
export const tailwindWidgets = {
  TextWidget,
  TextareaWidget,
  SelectWidget,
  DateWidget,
  UpDownWidget,
  FileWidget: FileUploadWidget,
};
